import React, { useContext, useState } from 'react';
import { ContextProps } from '../types/ContextProps';
import { ReportMetadata } from '../types/ViewModel/Verification/ReportMetadata';
import { DnvDataItem } from '../types/ViewModel/Verification/DnvDataItem';
import { Vessel } from '../types/ViewModel/Vessel/Vessel';
import { ReportComment } from '../types/ViewModel/Verification/ReportComment';
import { VerificationFilters } from '../types/ViewModel/Verification/VerificationFilters';
import { SelectOption } from '../types/ViewModel/SelectOption';

type VerificationContextType = {
    selectedReportMetadata: ReportMetadata | null;
    reportMetadataList: ReportMetadata[] | null;
    populatedReportMetadataList: ReportMetadata[] | null;
    filteredReportMetadataList: ReportMetadata[] | null;
    selectedReportMetadataList: ReportMetadata[] | null;
    verificationResponseList: DnvDataItem[] | null;
    selectedReportVessel: Vessel | null;
    reportComment: ReportComment | null;
    verificationFilter: VerificationFilters | null;
    numUnhandledReports: number;
    numTotalReports: number;
    numFilteredReports: number;
    isCommentExpanded: boolean | null;
    isDownloadDDExpanded: boolean | null;
    showConfirmAccept: boolean | null;
    selectedYears: SelectOption[];
    initialSelectedYears: SelectOption[];
    selectedUsers: SelectOption[];
    initialSelectedUsers: SelectOption[];
    selectedVessels: SelectOption[];
    initialSelectedVessels: SelectOption[];
    filteredVerificationVesselList: Vessel[];
    selectedManagers: SelectOption[];
    initialSelectedManagers: SelectOption[];
    selectedReportTypes: SelectOption[];
    initialSelectedReportTypes: SelectOption[];
    selectedReportStatuses: SelectOption[];
    initialSelectedReportStatuses: SelectOption[];
    selectedChecks: SelectOption[];
    availableChecks: SelectOption[];
    loadedStartYear: number | null;
    setReportMetadataList: (verificationPeriods: ReportMetadata[] | null) => void;
    setPopulatedReportMetadataList: (verificationPeriods: ReportMetadata[] | null) => void;
    setFilteredReportMetadataList: (verificationPeriods: ReportMetadata[] | null) => void;
    setSelectedReportMetadataList: (verificationPeriods: ReportMetadata[] | null) => void;
    setVerificationResponseList: (verificationPeriods: DnvDataItem[] | null) => void;
    setSelectedReportMetadata: (reportMetadata: ReportMetadata | null) => void;
    setSelectedReportVessel: (vessel: Vessel | null) => void;
    setReportComment: (reportComment: ReportComment | null) => void;
    setVerificationFilter: (verificationFilters: VerificationFilters | null) => void;
    setNumUnhandledReports: (unhandled: number) => void;
    setNumTotalReports: (unhandled: number) => void;
    setNumFilteredReports: (unhandled: number) => void;
    setIsCommentExpanded: (isExpanded: boolean | null) => void;
    setIsDownloadDDExpanded: (isExpanded: boolean | null) => void;
    setShowConfirmAccept: (isConfirm: boolean | null) => void;
    setSelectedYears: (selectedYears: SelectOption[]) => void;
    setInitialSelectedYears: (selectedYears: SelectOption[]) => void;
    setSelectedUsers: (selectedYears: SelectOption[]) => void;
    setInitialSelectedUsers: (selectedYears: SelectOption[]) => void;
    setSelectedVessels: (selectedYears: SelectOption[]) => void;
    setInitialSelectedVessels: (selectedYears: SelectOption[]) => void;
    setFilteredVerificationVesselList: (selectedVessels: Vessel[]) => void;
    setSelectedManagers: (selectedManagers: SelectOption[]) => void;
    setInitialSelectedManagers: (selectedManagers: SelectOption[]) => void;
    setSelectedReportTypes: (selectedManagers: SelectOption[]) => void;
    setInitialSelectedReportTypes: (selectedManagers: SelectOption[]) => void;
    setSelectedReportStatuses: (selectedManagers: SelectOption[]) => void;
    setInitialSelectedReportStatuses: (selectedManagers: SelectOption[]) => void;
    setSelectedChecks: (selected: SelectOption[]) => void;
    setAvailableChecks: (selected: SelectOption[]) => void;
    setLoadedStartYear: (year: number | null) => void;
};

export const VerificationContext = React.createContext<VerificationContextType>(undefined!);

export const VerificationProvider = ({ children }: ContextProps) => {
    const [reportMetadataList, setReportMetadataList] = useState<ReportMetadata[] | null>(null);
    const [verificationResponseList, setVerificationResponseList] = useState<DnvDataItem[] | null>(null);
    const [selectedReportMetadata, setSelectedReportMetadata] = useState<ReportMetadata | null>(null);
    const [populatedReportMetadataList, setPopulatedReportMetadataList] = useState<ReportMetadata[] | null>(null);
    const [filteredReportMetadataList, setFilteredReportMetadataList] = useState<ReportMetadata[] | null>(null);
    const [selectedReportMetadataList, setSelectedReportMetadataList] = useState<ReportMetadata[] | null>(null);
    const [selectedReportVessel, setSelectedReportVessel] = useState<Vessel | null>(null);
    const [reportComment, setReportComment] = useState<ReportComment | null>(null);
    const [verificationFilter, setVerificationFilter] = useState<VerificationFilters | null>(null);
    const [numUnhandledReports, setNumUnhandledReports] = useState<number>(0);
    const [numTotalReports, setNumTotalReports] = useState<number>(0);
    const [numFilteredReports, setNumFilteredReports] = useState<number>(0);
    const [isCommentExpanded, setIsCommentExpanded] = useState<boolean | null>(null);
    const [showConfirmAccept, setShowConfirmAccept] = useState<boolean | null>(null);
    const [selectedYears, setSelectedYears] = useState<SelectOption[]>([]);
    const [initialSelectedYears, setInitialSelectedYears] = useState<SelectOption[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<SelectOption[]>([]);
    const [initialSelectedUsers, setInitialSelectedUsers] = useState<SelectOption[]>([]);
    const [selectedVessels, setSelectedVessels] = useState<SelectOption[]>([]);
    const [initialSelectedVessels, setInitialSelectedVessels] = useState<SelectOption[]>([]);
    const [filteredVerificationVesselList, setFilteredVerificationVesselList] = useState<Vessel[]>([]);
    const [selectedManagers, setSelectedManagers] = useState<SelectOption[]>([]);
    const [initialSelectedManagers, setInitialSelectedManagers] = useState<SelectOption[]>([]);
    const [selectedReportTypes, setSelectedReportTypes] = useState<SelectOption[]>([]);
    const [initialSelectedReportTypes, setInitialSelectedReportTypes] = useState<SelectOption[]>([]);
    const [selectedReportStatuses, setSelectedReportStatuses] = useState<SelectOption[]>([]);
    const [initialSelectedReportStatuses, setInitialSelectedReportStatuses] = useState<SelectOption[]>([]);
    const [selectedChecks, setSelectedChecks] = useState<SelectOption[]>([]);
    const [availableChecks, setAvailableChecks] = useState<SelectOption[]>([]);
    const [isDownloadDDExpanded, setIsDownloadDDExpanded] = useState<boolean>(false);
    const [loadedStartYear, setLoadedStartYear] = useState<number | null>(null);

    return (
        <VerificationContext.Provider
            value={{
                selectedReportVessel,
                setSelectedReportVessel,
                reportMetadataList,
                setReportMetadataList,
                verificationResponseList,
                setVerificationResponseList,
                selectedReportMetadata,
                setSelectedReportMetadata,
                reportComment,
                setReportComment,
                verificationFilter,
                setVerificationFilter,
                populatedReportMetadataList,
                setPopulatedReportMetadataList,
                filteredReportMetadataList,
                setFilteredReportMetadataList,
                numUnhandledReports,
                setNumUnhandledReports,
                numTotalReports,
                setNumTotalReports,
                numFilteredReports,
                setNumFilteredReports,
                selectedReportMetadataList,
                setSelectedReportMetadataList,
                isCommentExpanded,
                setIsCommentExpanded,
                showConfirmAccept,
                setShowConfirmAccept,
                selectedYears,
                setSelectedYears,
                initialSelectedYears,
                setInitialSelectedYears,
                selectedUsers,
                setSelectedUsers,
                initialSelectedUsers,
                setInitialSelectedUsers,
                selectedVessels,
                setSelectedVessels,
                initialSelectedVessels,
                setInitialSelectedVessels,
                filteredVerificationVesselList,
                setFilteredVerificationVesselList,
                selectedManagers,
                setSelectedManagers,
                initialSelectedManagers,
                setInitialSelectedManagers,
                selectedReportTypes,
                setSelectedReportTypes,
                initialSelectedReportTypes,
                setInitialSelectedReportTypes,
                selectedReportStatuses,
                setSelectedReportStatuses,
                initialSelectedReportStatuses,
                setInitialSelectedReportStatuses,
                selectedChecks,
                setSelectedChecks,
                availableChecks,
                setAvailableChecks,
                isDownloadDDExpanded,
                setIsDownloadDDExpanded,
                loadedStartYear,
                setLoadedStartYear,
            }}
        >
            {children}
        </VerificationContext.Provider>
    );
};

export const useVerificationStore = () => useContext(VerificationContext);
